import { Button } from '@/components/Elements/Button';
import { Callout } from '@/components/Elements/Callout';
import { ErrorMessage } from '@/components/Elements/ErrorMessage';
import { InputField } from '@/components/Form/InputField';
import { PasswordField } from '@/components/Form/PasswordField';
import { ReadOnlyField } from '@/components/Form/ReadOnlyField';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEmailChangeForm } from './useEmailChangeForm';

type EmailChangeSectionProps = {
  currentEmail?: string | null;
  defaultExpanded?: boolean;
};

export const EmailChangeSection = ({
  currentEmail,
  defaultExpanded = false,
}: EmailChangeSectionProps) => {
  const { t } = useTranslation();
  const form = useEmailChangeForm(currentEmail);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <section className="space-y-3">
      <div className="space-y-2">
        <ReadOnlyField
          label={t('メールアドレス')}
          value={currentEmail ?? ''}
          appearance="design-system"
        />
        {!isExpanded && (
          <div className="flex justify-end">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setIsExpanded(true)}
            >
              {t('メールアドレスを変更')}
            </Button>
          </div>
        )}
      </div>
      {form.requestedEmail && (
        <Callout variant="success" size="compact">
          {t('確認メールを送信しました。')}
        </Callout>
      )}
      {isExpanded && (
        <div className="space-y-3">
          <InputField
            type="text"
            inputMode="email"
            label={t('新しいメールアドレス')}
            appearance="design-system"
            value={form.email}
            autoComplete="email"
            error={Boolean(form.fieldErrors.email?.length)}
            errors={form.fieldErrors.email}
            onChange={(event) => form.setEmail(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void form.onSubmit();
              }
            }}
          />
          <PasswordField
            label={t('現在のパスワード')}
            appearance="design-system"
            value={form.currentPassword}
            autoComplete="current-password"
            error={Boolean(form.fieldErrors.currentPassword?.length)}
            errors={form.fieldErrors.currentPassword}
            onChange={(event) => form.setCurrentPassword(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void form.onSubmit();
              }
            }}
          />
          <ErrorMessage errors={form.globalErrors} />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(false)}
            >
              {t('キャンセル')}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              isLoading={form.isSaving}
              disabled={!form.canSubmit}
              onClick={() => void form.onSubmit()}
            >
              {t('確認メールを送信')}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};
